/**
 * useGenerationState — Sprint 5 · Project Symphony
 *
 * Live polling hook for the /generation endpoint. Executive rules:
 *
 * * Only polls while the campaign status is Generating.
 * * Stops immediately on Generation Complete, failure, route change,
 *   or component unmount.
 * * Never polls an inactive campaign.
 * * Does NOT reset scroll or remount the parent.
 * * Contract is compatible with a future SSE replacement — swap only
 *   this hook.
 */
import { useQuery } from "@tanstack/react-query";
import { getGenerationState } from "./api";
import type { GenerationState } from "./types";

const POLL_INTERVAL_MS = 3000;

export function useGenerationState(
  blueprintId: string,
  {
    activeStatus,
    enabled = true,
  }: { activeStatus: string | undefined; enabled?: boolean },
) {
  const isActive =
    activeStatus === "generating" || activeStatus === "generation_complete";
  return useQuery<GenerationState>({
    queryKey: ["campaign-generation", blueprintId],
    queryFn: () => getGenerationState(blueprintId),
    enabled: enabled && Boolean(blueprintId) && isActive,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return POLL_INTERVAL_MS;
      // Stop the poll as soon as the campaign is no longer active.
      return data.is_active ? POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    // Keep the previously-fetched state visible while a new poll is
    // in flight — no flicker.
    placeholderData: (prev) => prev,
  });
}
